import LearningJourney from './LearningJourney';
import { SegmentFilter } from '../segmentFilters';
import { StatItemProps } from './SegmentItem';
import { SegmentedBar, StatRow, WIDGET_COLORS, WidgetCard, WidgetTooltip, currencyIcon } from './WidgetPrimitives';

// Shared by the bar segment and the stat below it so both toggle the same filter.
const currencyFilter = (status: string, label: string): SegmentFilter => ({
  attribute: 'professional_currency_status',
  value: status,
  group: 'Professional Currency',
  label
});

const currencyStat = (
  percent: number,
  status: string,
  label: string,
  color?: string
): StatItemProps => ({
  value: `${percent}%`,
  label,
  color,
  filter: currencyFilter(status, label)
});

export interface ProfessionalCurrencyWidgetData {
  total_count?: number;
  counts?: {
    current?: number;
    on_track?: number;
    in_progress?: number;
    not_started?: number;
  };
  percentages?: {
    current?: number;
    on_track?: number;
    in_progress?: number;
    not_started?: number;
  };
  lj_counts?: {
    current?: number;
    on_track?: number;
    in_progress?: number;
    not_started?: number;
  };
  lj_percentages?: {
    current?: number;
    on_track?: number;
    in_progress?: number;
    not_started?: number;
  };
  lj_total_count?: number;
}

interface ProfessionalCurrencyWidgetProps {
  data: ProfessionalCurrencyWidgetData;
}

const TOOLTIP = (
  <WidgetTooltip
    heading="Professional Currency"
    description="Shows Educator progress towards their required Professional Currency target for the current calendar year."
    definitions={[
      {
        term: 'Current',
        description: 'Educators who have met or exceeded their required points target for the calendar year.',
        color: WIDGET_COLORS.green
      },
      {
        term: 'On Track',
        description:
          'Educators who have registered sufficient points to meet their requirements, but program or workshop dates are still pending.',
        color: WIDGET_COLORS.blue
      },
      {
        term: 'In Progress',
        description:
          'Educators who have obtained some points but have not yet met the total points requirement for their employment type.',
        color: WIDGET_COLORS.lightBlue
      },
      {
        term: 'Not Started',
        description: 'Educators who have not obtained or registered any points during the current calendar year.'
      }
    ]}
    note="Learning Journeys are also displayed where applicable."
  />
);

const ProfessionalCurrencyWidget = ({
  data: {
    total_count: total = 0,
    counts: {
      current: currentCount = 0,
      on_track: onTrackCount = 0,
      in_progress: inProgressCount = 0,
      not_started: notStartedCount = 0
    } = {},
    percentages: {
      current: currentPercent = 0,
      on_track: onTrackPercent = 0,
      in_progress: inProgressPercent = 0,
      not_started: notStartedPercent = 0
    } = {},
    lj_counts: {
      current: ljCurrentCount = 0,
      on_track: ljOnTrackCount = 0,
      in_progress: ljInProgressCount = 0,
      not_started: ljNotStartedCount = 0
    } = {},
    lj_percentages: {
      current: ljCurrentPercent = 0,
      on_track: ljOnTrackPercent = 0,
      in_progress: ljInProgressPercent = 0,
      not_started: ljNotStartedPercent = 0
    } = {},
    lj_total_count: ljTotalCount = 0
  }
}: ProfessionalCurrencyWidgetProps) => (
  <WidgetCard title="Professional Currency" total={total} icon={currencyIcon} isRoundedIcon={true} tooltip={TOOLTIP}>
    <SegmentedBar
      segments={[
        {
          percent: currentPercent,
          color: WIDGET_COLORS.green,
          label: 'Current',
          count: currentCount,
          filter: currencyFilter('current', 'Current')
        },
        {
          percent: onTrackPercent,
          color: WIDGET_COLORS.blue,
          label: 'On track',
          count: onTrackCount,
          filter: currencyFilter('on_track', 'On track')
        },
        {
          percent: inProgressPercent,
          color: WIDGET_COLORS.lightBlue,
          label: 'In progress',
          count: inProgressCount,
          filter: currencyFilter('in_progress', 'In progress')
        }
      ]}
      remainder={{
        label: 'Not started',
        percent: notStartedPercent,
        count: notStartedCount,
        filter: currencyFilter('not_started', 'Not started')
      }}
    />
    <StatRow
      items={[
        currencyStat(currentPercent, 'current', 'Current', WIDGET_COLORS.green),
        currencyStat(onTrackPercent, 'on_track', 'On track', WIDGET_COLORS.blue),
        currencyStat(inProgressPercent, 'in_progress', 'In progress', WIDGET_COLORS.lightBlue),
        currencyStat(notStartedPercent, 'not_started', 'Not started')
      ]}
    />
    {ljTotalCount > 0 ? <LearningJourney
      attribute="professional_learning_journey_status"
      group="Professional Learning Journey"
      learningJourney={{
      total_count: ljTotalCount,
      counts: {
        current: ljCurrentCount,
        on_track: ljOnTrackCount,
        in_progress: ljInProgressCount,
        not_started: ljNotStartedCount
      },
      percentages: {
        current: ljCurrentPercent,
        on_track: ljOnTrackPercent,
        in_progress: ljInProgressPercent,
        not_started: ljNotStartedPercent
      }
    }} /> : null}
  </WidgetCard>
);

export default ProfessionalCurrencyWidget;
